import { Controller } from "@hotwired/stimulus"

// "Scan the org" reveal for the cold-path signup preview (Item 1, Stage 2).
//
// Two modes:
//   • FULL  — the complete payload is embedded (direct visit / cache hit). The header builds in,
//             then the sampled people flicker and settle into the gated summary.
//   • LIVE  — `liveUrl` is set. The header is painted server-side (instant, from the Crunchbase
//             directory) and reveals; the card polls #data and STREAMS the people in on "complete".
//             No frame reloads — the card mutates in place.
//
// No counts are shown (Crunchbase's employee count is unreliable; the scanned sample undersells) —
// the settled card is just the firmographics + the gated payoff line. Names are first-name/initial
// only — no last name ever reaches the DOM. Honors reduced-motion.
export default class extends Controller {
  static targets = ["status", "slot", "monogram", "logo", "company", "meta"]
  static values = {
    name: String,
    people: Array,                          // [{name_label, title, photo_url}] — full mode
    domain: String,
    liveUrl: String,
    scanMs: { type: Number, default: 3400 }, // how long the people flicker before settling
    buildMs: { type: Number, default: 560 }, // header assembles before the people scan
    pollMs: { type: Number, default: 1200 },
    timeoutMs: { type: Number, default: 30000 },
  }

  connect() {
    this.badPhotos = new Set()
    this.setupPhotoFallback()
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.people = []

    if (this.live) {
      this.element.classList.add("is-live")
      this.headed = this.companyPainted() // header painted server-side from the directory
      if (this.headed) {
        this.element.classList.add("is-headed")
        if (!this.reduced) this.element.classList.add("is-building") // play the reveal on the server-painted header
      }
      this.deadline = Date.now() + this.timeoutMsValue
      this.poll()
    } else {
      this.people = Array.isArray(this.peopleValue) ? this.peopleValue : []
      this.headed = true
      this.element.classList.add("is-headed")
      if (!this.reduced) this.element.classList.add("is-building")
      this.runScan(this.buildMsValue)
    }
  }

  disconnect() {
    clearTimeout(this.buildTimer)
    clearTimeout(this.settleTimer)
    clearTimeout(this.pollTimer)
    clearTimeout(this.flickerTimer)
  }

  get live() {
    return this.hasLiveUrlValue && this.liveUrlValue.length > 0
  }

  companyPainted() {
    return this.hasCompanyTarget && this.companyTarget.textContent.trim().length > 0
  }

  // ── LIVE: poll #data, stream the people in on "complete" ────────────────────
  async poll() {
    if (Date.now() > this.deadline) return this.fail()
    let data = {}
    try {
      const res = await fetch(this.liveUrlValue, { headers: { Accept: "application/json" } })
      if (res.ok) data = await res.json()
    } catch (_e) { /* transient network blip — keep polling */ }

    if (data.phase === "fallback") return this.fail()
    if (data.company && !this.headed) this.paintHeader(data.company)
    if (data.phase === "complete") return this.complete(data)
    this.pollTimer = setTimeout(() => this.poll(), this.pollMsValue)
  }

  complete(data) {
    clearTimeout(this.pollTimer)
    if (data.company) this.paintHeader(data.company)
    this.people = Array.isArray(data.people) ? data.people : []
    this.runScan(this.headed ? 120 : this.buildMsValue) // header's already up → go almost straight to the scan
  }

  // Reveal the company header (logo → name → industry) the moment it lands.
  paintHeader(c) {
    this.headed = true
    this.element.classList.add("is-headed")
    if (this.hasCompanyTarget && c.name) this.companyTarget.textContent = c.name
    if (this.hasMetaTarget) this.metaTarget.textContent = c.meta || ""
    if (this.hasMonogramTarget && c.name) this.monogramTarget.textContent = (c.name.trim()[0] || "").toUpperCase()
    if (this.hasStatusTarget) this.statusTarget.textContent = `Scanning ${c.name || this.domainValue}…`
    if (this.hasLogoTarget) this.paintLogo(c.logo_url)
    if (!this.reduced) this.element.classList.add("is-building") // staged logo→name→meta
  }

  paintLogo(url) {
    const img = this.logoTarget
    if (!url) { img.classList.remove("is-loaded"); img.classList.add("hidden"); return }
    img.classList.remove("hidden")
    img.onload = () => img.classList.add("is-loaded")
    img.onerror = () => { img.classList.remove("is-loaded"); img.classList.add("hidden") } // reveal monogram
    if (img.getAttribute("src") !== url) img.setAttribute("src", url)
    else img.classList.add("is-loaded")
  }

  // A stalled / no-roster build → swap the frame for the graceful fallback.
  fail() {
    clearTimeout(this.pollTimer)
    const frame = this.element.closest("turbo-frame")
    if (!frame) return
    const url = new URL(frame.src, window.location.origin)
    url.searchParams.set("timed_out", "1")
    frame.src = url.toString()
  }

  // ── people flicker → settle ─────────────────────────────────────────────────
  runScan(delay) {
    if (this.reduced || this.people.length === 0) { this.settle(); return }
    this.buildTimer = setTimeout(() => {
      this.startedAt = performance.now()
      this.scheduleFlicker()
      this.settleTimer = setTimeout(() => this.settle(), this.scanMsValue)
    }, delay)
  }

  scheduleFlicker() {
    this.flickerSlots()
    const progress = Math.min(1, (performance.now() - this.startedAt) / this.scanMsValue)
    const interval = 45 + progress * progress * 200 // 45ms → ~245ms, decelerating
    this.flickerTimer = setTimeout(() => this.scheduleFlicker(), interval)
  }

  flickerSlots() {
    this.slotTargets.forEach((slot) => this.paint(slot, this.people[Math.floor(Math.random() * this.people.length)]))
  }

  setupPhotoFallback() {
    this.slotTargets.forEach((slot) => {
      const img = slot.querySelector("[data-role='avatar-img']")
      if (!img) return
      img.addEventListener("load", () => { if (img.getAttribute("src")) img.classList.add("is-loaded") })
      img.addEventListener("error", () => {
        const src = img.getAttribute("src")
        if (src) this.badPhotos.add(src)
        img.classList.remove("is-loaded")
        img.removeAttribute("src")
      })
    })
  }

  paint(slot, person) {
    if (!person) return
    const label = (person.name_label || "").trim()
    const initial = slot.querySelector("[data-role='avatar-initial']")
    const name = slot.querySelector("[data-role='name']")
    const title = slot.querySelector("[data-role='title']")
    if (initial) initial.textContent = (label[0] || "•").toUpperCase()
    this.paintPhoto(slot.querySelector("[data-role='avatar-img']"), person.photo_url)
    if (name) name.textContent = label
    if (title) title.textContent = person.title || ""
  }

  paintPhoto(img, url) {
    if (!img) return
    if (url && !this.badPhotos.has(url)) {
      if (img.getAttribute("src") !== url) img.setAttribute("src", url)
    } else {
      img.classList.remove("is-loaded")
      img.removeAttribute("src")
    }
  }

  settle() {
    clearTimeout(this.buildTimer)
    clearTimeout(this.settleTimer)
    clearTimeout(this.flickerTimer)
    const n = Math.max(this.people.length, 1)
    this.slotTargets.forEach((slot, i) => this.paint(slot, this.people[i % n]))
    const who = (this.hasCompanyTarget && this.companyTarget.textContent.trim()) || this.nameValue || this.domainValue
    if (this.hasStatusTarget && who) this.statusTarget.textContent = `${who} mapped`
    this.element.classList.add("is-settled")
  }
}
